// 면접 평가지 모달 — 스케줄 모달 위에 한 겹 더. 저장 버튼 없이 변경 즉시 디바운스 자동 저장.
// 평가 담당자는 서버(/dashboard/interview/update)가 로그인 사용자로 기록한다.
import React, { useEffect, useRef, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import { toast } from 'react-toastify';
import styles from '../../componentsStyle/interviewschedule.module.css';
import { postFetch } from '../../../fetch/fetch';
import { EVAL_LISTS, InterviewItem, categoryOf, fmtWhen, shortLocation } from './interviewconstants';
import { captureEvaluation } from './interviewcapture';
import InterviewSelect from './interviewselect';

const evalBoxStyle: any = {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 'calc(100% - 48px)', maxWidth: 520, maxHeight: 'calc(100vh - 48px)', overflowY: 'auto',
    bgcolor: '#fff', borderRadius: '16px', boxShadow: 24, outline: 'none',
    p: '20px 22px 22px', display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box'
};

const STATUS = [
    { label: "예정", v: 0, color: "#3346A6" },
    { label: "면접 완료", v: 1, color: "#17643a" },
    { label: "노쇼", v: 2, color: "#a32218" },
];

const SAVE_DELAY = 500;

interface Props {
    item: InterviewItem;
    user: any;
    onLocalChange: (id: number, patch: Partial<InterviewItem>) => void;
    onDeleted: (id: number) => void;
    onClose: () => void;
}

const InterviewEvalModal: React.FC<Props> = ({ item, user, onLocalChange, onDeleted, onClose }) => {

    const [capMsg, setCapMsg] = useState("");
    const [capImg, setCapImg] = useState("");
    const pendingRef = useRef<any>({});
    const timerRef = useRef<any>(null);

    const flush = async () => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        const patch = pendingRef.current;
        pendingRef.current = {};
        if (!Object.keys(patch).length) return;
        try {
            const result: any = await postFetch("/dashboard/interview/update", { id: item.id, ...patch });
            if (result.message === "success") {
                if (result.evaluatorName) onLocalChange(item.id, { evaluatorName: result.evaluatorName });
            } else {
                toast.error("평가 저장에 실패했습니다.");
            }
        } catch (e) {
            console.log(e);
            toast.error("평가 저장에 실패했습니다.");
        }
    };

    // 닫힘/언마운트 시 남은 변경 저장
    useEffect(() => {
        return () => { flush(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 화면은 즉시 반영, 서버는 디바운스로 모아서 저장
    const change = (patch: Partial<InterviewItem>) => {
        onLocalChange(item.id, patch);
        pendingRef.current = { ...pendingRef.current, ...patch };
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flush, SAVE_DELAY);
    };

    const category = categoryOf(item);
    const evalItems = category ? EVAL_LISTS[category] : [];

    const pickGrade = (i: number, g: string) => {
        const grades = evalItems.map((_, idx) => item.grades[idx] || "");
        grades[i] = grades[i] === g ? "" : g;
        change({ grades });
    };

    const capture = async () => {
        if (!category) { setCapMsg("평가 리스트 종류를 먼저 고르세요."); setCapImg(""); return; }
        await flush();
        const result = await captureEvaluation(item, category, item.evaluatorName || user?.name || "");
        if (result.ok) {
            setCapMsg("클립보드에 복사했습니다. 카톡방에 붙여넣기 하세요.");
            setCapImg("");
        } else {
            setCapMsg("이 환경에서는 클립보드 복사가 안 돼서 아래에 이미지를 띄웠습니다. 우클릭해서 복사하세요.");
            setCapImg(result.dataUrl);
        }
    };

    const remove = async () => {
        if (!window.confirm(`${item.name} 면접을 삭제할까요?`)) return;
        pendingRef.current = {};
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        const result: any = await postFetch("/dashboard/interview/delete", { id: item.id });
        if (result.message === "success") {
            onDeleted(item.id);
        } else {
            toast.error("삭제에 실패했습니다.");
        }
    };

    return (
        <Modal open onClose={onClose} sx={{ zIndex: 700 }}>
            <Box sx={evalBoxStyle} className={styles.wrap} role="dialog" aria-label="면접 평가">
                <div className={styles.evalHead}>
                    <div>
                        <div className={styles.evalNameRow}>
                            <div className={styles.evalName}>{item.name}</div>
                            <button className={styles.captureBtn} onClick={capture} title="평가지를 이미지로 복사">
                                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3346A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 8h3l2-3h6l2 3h3v11H4z"></path><circle cx="12" cy="13" r="3.5"></circle></svg>
                                캡처
                            </button>
                        </div>
                        <div className={styles.evalSub}><span className={styles.loc}>{shortLocation(item.location)}</span> {item.job} · {fmtWhen(item)}</div>
                    </div>
                    <button className={styles.circleBtn} aria-label="닫기" onClick={onClose}>
                        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333333" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"></path></svg>
                    </button>
                </div>

                <div className={styles.catRow}>
                    <label htmlFor="interview-cat-select" className={styles.catLabel}>평가 리스트</label>
                    <InterviewSelect id="interview-cat-select" value={category} height={40} fullWidth
                        options={[{ value: "", label: "미정 (근무 종류 선택)" }, { value: "당직", label: "당직" }, { value: "사감", label: "사감" }, { value: "데스크", label: "데스크" }]}
                        onChange={(v) => change({ category: v, grades: [] })} />
                </div>

                <div className={styles.statusRow}>
                    {STATUS.map((s) => {
                        const active = item.status === s.v;
                        return (
                            <button key={s.v} className={styles.statusBtn} onClick={() => change({ status: s.v })}
                                style={active ? { borderColor: s.color, background: s.color, color: "#fff" } : undefined}>
                                {s.label}
                            </button>
                        );
                    })}
                </div>

                {!category ? (
                    <div className={styles.noCat}>위에서 평가 리스트 종류를 고르면 항목이 나옵니다.</div>
                ) : (
                    <div className={styles.evalTable}>
                        <div className={styles.evalTableHead}>
                            <div className={styles.evalTableTitle}>{category} 평가 리스트</div>
                            <div className={styles.evalTableSub}>평가 (ABCDE)</div>
                        </div>
                        <div className={styles.evalRows}>
                            {evalItems.map((text, i) => {
                                const opts = text.indexOf("(P/F)") >= 0 ? ["P", "F"] : ["A", "B", "C", "D", "E"];
                                return (
                                    <div key={i} className={styles.evalRow}>
                                        <div className={styles.evalText}><span className={styles.evalNo}>{i + 1}</span><span>{text}</span></div>
                                        <div className={styles.optRow}>
                                            {opts.map((o) => (
                                                <button key={o} className={`${styles.optBtn} ${item.grades[i] === o ? styles.optBtnActive : ""}`} onClick={() => pickGrade(i, o)}>{o}</button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <label className={styles.memoLabel}>메모
                    <textarea className={styles.memo} rows={3} placeholder="면접 중 느낀 점, 특이사항" value={item.memo || ""} onChange={(e) => change({ memo: e.target.value })} />
                </label>

                <div className={styles.evalFoot}>
                    <span>평가 담당자: {user?.name} · 입력하면 바로 자동 저장됩니다.</span>
                    <button className={styles.deleteBtn} onClick={remove}>삭제</button>
                </div>

                {capMsg ? <div className={styles.capMsg}>{capMsg}</div> : null}
                {capImg ? <img className={styles.capImg} src={capImg} alt="평가지 캡처 이미지" /> : null}
            </Box>
        </Modal>
    );
};

export default InterviewEvalModal;
